import React, { useState, useRef } from 'react';
import { Info, ShieldCheck, CheckCircle, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal, Button, FormInput, FormTextarea, FileUpload, Card } from '../../../../components/ui';
import { SectionTitle, Caption } from '../../../../components/ui/Typography';
import { siteSurveySchema } from "../validations/siteSurvey.schema";

export const InspectionAddForm = ({ initialData = {}, onSubmit, onCancel, loading = false, hideActions = false }) => {
  const [formData, setFormData] = useState({
    floorDetails: initialData.floorDetails || '',
    existingPowerConsumption: initialData.existingPowerConsumption || '',
    VerveCapacityRequested: initialData.VerveCapacityRequested || initialData.capacity || '',
    existingPowerPaid: initialData.existingPowerPaid || '',
    notes: initialData.inspectionNote || '',
    files: []
  });
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    const maxSize = 100 * 1024 * 1024; // 100MB

    if (formData.files.length + selectedFiles.length > 25) {
      toast.error("Maximum 25 files allowed");
      return;
    }

    selectedFiles.forEach(file => {
      const isAllowedType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
      const isUnderSize = file.size <= maxSize;

      if (!isAllowedType) {
        toast.error(`${file.name} is not a supported file type (Images/PDF only)`);
      } else if (!isUnderSize) {
        toast.error(`${file.name} exceeds 100MB limit`);
      } else {
        validFiles.push(file);
      }
    });

    if (validFiles.length > 0) {
      setFormData(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const result = siteSurveySchema.safeParse(formData);
    if (!result.success) {
      toast.error(result.error.errors[0].message);
      return;
    }

    onSubmit(result.data);
  };

  return (
    <Card padding="md" className="animate-in fade-in slide-in-from-bottom-2">
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-1.5 bg-brand-blue/10 text-brand-blue rounded-md text-lg shadow-sm border border-brand-blue/5 flex items-center justify-center">📝</div>
          <div className="flex flex-col">
            <SectionTitle>Site Survey Details</SectionTitle>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
          <FormInput
            label="Floor Details"
            name="floorDetails"
            placeholder="e.g. G+2, Rooftop"
            value={formData.floorDetails}
            onChange={handleChange}
            required
            size="sm"
            icon={<Info size={14} />}
            labelClassName="label text-xs"
            inputClassName="input text-sm"
          />

          <FormInput
            label="Existing Consumption (Units/mo)"
            type="number"
            name="existingPowerConsumption"
            placeholder="e.g. 500"
            value={formData.existingPowerConsumption}
            onChange={handleChange}
            required
            size="sm"
            icon={<ShieldCheck size={14} />}
            labelClassName="label text-xs"
            inputClassName="input text-sm"
          />

          <FormInput
            label="Recommended Capacity (kW)"
            type="number"
            name="VerveCapacityRequested"
            placeholder="e.g. 5"
            value={formData.VerveCapacityRequested}
            onChange={handleChange}
            required
            size="sm"
            icon={<CheckCircle size={14} />}
            labelClassName="label text-xs"
            inputClassName="input text-sm"
          />

          <FormInput
            label="Last Bill Amount (₹)"
            type="number"
            name="existingPowerPaid"
            placeholder="e.g. 3500"
            value={formData.existingPowerPaid}
            onChange={handleChange}
            required
            size="sm"
            icon={<DollarSign size={14} />}
            labelClassName="label text-xs"
            inputClassName="input text-sm"
          />
        </div>

        <FormTextarea
          label="Inspection Notes / Observations"
          name="notes"
          placeholder="Detailed observations from site visit (roof condition, shading, wiring path...)"
          value={formData.notes || ''}
          onChange={handleChange}
          rows={3}
          size="sm"
          labelClassName="label text-xs"
          inputClassName="input text-sm"
        />

        <FileUpload
          label="Site Photos & Documents"
          files={formData.files}
          onFileChange={handleFileChange}
          onRemove={removeFile}
          onPreview={setPreviewFile}
          maxFiles={25}
        />

        <Modal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          title={`Preview: ${previewFile?.name}`}
          size="xl"
        >
          <div className="flex flex-col items-center justify-center min-h-[500px] bg-slate-50/30 p-2 rounded-xl">
            {previewFile && (
              previewFile.type === 'application/pdf' ? (
                <iframe
                  src={URL.createObjectURL(previewFile)}
                  className="w-full h-[75vh] rounded-xl border border-slate-100 shadow-2xl"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={URL.createObjectURL(previewFile)}
                  alt="Preview"
                  className="max-w-full max-h-[75vh] rounded-xl shadow-2xl border border-white object-contain"
                />
              )
            )}
          </div>
        </Modal>

        {!hideActions && (
          <div className="flex gap-3 pt-6 mt-6 border-t border-slate-50">
            <Button
              type="button"
              onClick={handleSubmit}
              loading={loading}
              className="btn-orange flex-1 py-2.5 flex items-center justify-center gap-2"
            >
              Save & Mark Inspected
            </Button>
            {onCancel && (
              <Button
                onClick={onCancel}
                variant="secondary"
                className="px-6 py-2.5 text-slate-600 border-slate-200 hover:bg-transparent hover:text-slate-800 focus:bg-transparent active:bg-transparent shadow-none"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
